/**
 * Catalogue des modules extras (Petit-déj / Bain de fleurs / Décor)
 * + helpers pour la shopping list consolidée + progression par module.
 * Port fidèle de constants/modules.ts du RG original.
 */

import { parseBreakfastForm } from "./rgBreakfastParser";

function hasProtein(f, kind) {
  const vs = f?.voyageurs || [];
  if (vs.length === 0) return true;
  return vs.some((v) => (v?.protein || "").toLowerCase().includes(kind));
}
function hasBoisson(f, kind) {
  const vs = f?.voyageurs || [];
  if (vs.length === 0) return true;
  const keywords = kind === "cafe" ? ["café", "cafe"] : ["thé", "the"];
  return vs.some((v) => {
    const b = (v?.boisson || "").toLowerCase();
    return keywords.some((k) => b.includes(k));
  });
}
function hasConfiture(f, name) {
  const chosen = (f?.confitures || []).map((s) => s.toLowerCase());
  if (chosen.length === 0) return true;
  return chosen.some((c) => c.includes(name.toLowerCase()));
}

// ============ PETIT DÉJEUNER ============
export const BREAKFAST_ITEMS = [
  { key: "oeufs", icon: "🥚", label: "Œufs x6", source: "market", buyLabel: () => "6 œufs", group: "produits" },
  { key: "bacon", icon: "🥓", label: "Bacon x4 tranches", source: "market", group: "produits", visible: (f) => hasProtein(f, "bacon"), buyLabel: () => "4 tranches de bacon" },
  { key: "fromage", icon: "🧀", label: "Fromages x3", source: "market", group: "produits", visible: (f) => hasProtein(f, "fromage"), buyLabel: () => "3 fromages" },
  { key: "pain", icon: "🥖", label: "Pain x1 (matin même)", source: "morning", buyLabel: () => "1 pain (matin même)", group: "produits" },
  { key: "fruits", icon: "🍓", label: "3 fruits du marché différents", source: "market", group: "produits", buyLabel: () => "3 fruits différents" },
  { key: "oranges", icon: "🍊", label: "Oranges x6", source: "market", buyLabel: () => "6 oranges", group: "produits" },
  { key: "beurre", icon: "🧈", label: "Beurre maison", source: "market", group: "produits",
    ingredients: ["Crème liquide 33%", "Gros sel de Guérande"],
    buyLabel: (f) => `Beurre maison ${f?.beurre_type === "thym" ? "(thym)" : "(nature)"}`,
  },
  { key: "confiture_ananas_passion", icon: "🍍", label: "Confiture ananas passion", source: "market", group: "produits", visible: (f) => hasConfiture(f, "ananas"), buyLabel: () => "Confiture ananas passion" },
  { key: "confiture_mangue_passion", icon: "🥭", label: "Confiture mangue passion", source: "market", group: "produits", visible: (f) => hasConfiture(f, "mangue"), buyLabel: () => "Confiture mangue passion" },
  { key: "confiture_tomate", icon: "🍅", label: "Confiture tomate", source: "market", group: "produits", visible: (f) => hasConfiture(f, "tomate"), buyLabel: () => "Confiture tomate" },
  { key: "pancakes", icon: "🥞", label: "Ingrédients pancakes", source: "market", group: "produits",
    ingredients: ["Farine 250g", "Beurre doux 50g", "Sucre roux 75g", "Sucre vanillé", "Levure chimique", "Arôme vanille"],
    buyLabel: () => "Ingrédients pancakes",
  },
  { key: "chocolat", icon: "🍫", label: "Chocolat à tartiner", source: "market", group: "produits", buyLabel: () => "Chocolat à tartiner", hint: "À terme, définir une marque" },
  { key: "cafe", icon: "☕", label: "Café", source: "market", group: "produits", visible: (f) => hasBoisson(f, "cafe"), buyLabel: () => "Café", hint: "À terme, définir un café standard du refuge" },
  { key: "the", icon: "🍵", label: "Thé", source: "market", group: "produits", visible: (f) => hasBoisson(f, "the"), buyLabel: () => "Thé" },
  { key: "lait", icon: "🥛", label: "Lait", source: "market", buyLabel: () => "Lait", group: "produits" },
];

export const BREAKFAST_PREP_ITEMS = [
  { key: "plateau", icon: "🍽", label: "Plateau dressé", group: "preparation" },
  { key: "serviettes", icon: "🪡", label: "Serviettes propres", group: "preparation" },
  { key: "couverts", icon: "🍴", label: "Couverts propres", group: "preparation" },
  { key: "vaisselle", icon: "🍵", label: "Vaisselle prête", group: "preparation" },
];

// ============ BAIN DE FLEURS ============
export const BAINFLEURS_ITEMS = [
  { key: "roses", icon: "🌸", label: "20 roses roses pâles", source: "market", buyLabel: () => "20 roses roses pâles", group: "fleurs" },
  { key: "fleurs_blanches", icon: "🌼", label: "Fleurs blanches", source: "garden", group: "fleurs" },
  { key: "feuillage", icon: "🌿", label: "Feuillage (romarin, etc.)", source: "garden", group: "fleurs" },
  { key: "bougainvillier", icon: "🪻", label: "Bougainvillier séché", source: "garden", hasDryOption: true, group: "fleurs" },
  { key: "bougies", icon: "🕯", label: "Bougies (si prévu)", group: "fleurs" },
  { key: "sel_de_bain", icon: "🧂", label: "Sel de bain", group: "sel" },
];

// ============ DÉCOR DU REFUGE ============
export const DECOR_ITEMS = [
  { key: "roses_decor", icon: "🌹", label: "8 roses", source: "market",
    buyLabel: (_f, decorType) => decorType === "nuit_rouge" ? "8 roses rouges" : "8 roses roses",
    group: "fleurs",
  },
  { key: "tissus", icon: "🪡", label: "Tissus", group: "decor" },
  { key: "chandeliers", icon: "🕎", label: "2 chandeliers", group: "decor" },
  { key: "bougies_grandes", icon: "🕯", label: "Bougies grandes x3", group: "decor" },
  { key: "bougies_petites", icon: "🕯", label: "Bougies petites (tout le stock)", group: "decor" },
  { key: "blue_tac", icon: "🔵", label: "Blue tac", group: "decor" },
  { key: "piles", icon: "🔋", label: "Piles x6", group: "decor" },
];

export const DECOR_TYPE_LABELS = { nuit_rouge: "Nuit rouge", nuit_rose: "Nuit rose" };

/**
 * Shopping list consolidée : agrège les items market des 3 modules
 * dont l'état n'est PAS "stock". Utilise le parser petit-déj pour filtrer
 * uniquement ce que le voyageur a demandé (protéines, boissons, confitures).
 */
export function buildShoppingList(reservation) {
  const states = reservation?.module_states || {};
  const breakfastState = states.petit_dej || {};
  const bainState = states.bain_fleurs || {};
  const decorState = states.decor || {};
  const parsedNote = parseBreakfastForm(reservation?.notes);
  const bfForm = reservation?.breakfast_form || {};
  const form = bfForm.received ? {
    voyageurs: bfForm.voyageurs || [],
    confitures: Array.isArray(bfForm.confitures) ? bfForm.confitures : [],
    beurre_type: bfForm.beurre_type || ((bfForm.beurre || "").toLowerCase().includes("thym") ? "thym" : "nature"),
  } : {
    voyageurs: parsedNote.voyageurs,
    confitures: parsedNote.confitures,
    beurre_type: (parsedNote.beurre || "").toLowerCase().includes("thym") ? "thym" : "nature",
  };
  const decorType = reservation?.decor_type;
  const extras = reservation?.extras || [];
  const out = [];

  if (extras.includes("petit_dejeuner")) {
    BREAKFAST_ITEMS.forEach((it) => {
      if (it.visible && !it.visible(form, decorType)) return;
      if (it.source !== "market") return;
      if (breakfastState[it.key] === "stock") return;
      out.push({ moduleKey: "petit_dej", itemKey: it.key, icon: it.icon, label: it.label });
    });
  }
  if (extras.includes("bain_fleurs")) {
    BAINFLEURS_ITEMS.forEach((it) => {
      if (it.source !== "market") return;
      if (bainState[it.key] === "stock") return;
      out.push({ moduleKey: "bain_fleurs", itemKey: it.key, icon: it.icon, label: it.label });
    });
  }
  if (extras.includes("decor")) {
    DECOR_ITEMS.forEach((it) => {
      if (it.source !== "market") return;
      if (decorState[it.key] === "stock") return;
      out.push({ moduleKey: "decor", itemKey: it.key, icon: it.icon, label: it.label });
    });
  }
  return out;
}

export function moduleProgress(reservation, moduleKey) {
  const state = reservation?.module_states?.[moduleKey] || {};
  const form = reservation?.breakfast_form || {};
  const decorType = reservation?.decor_type;
  let items = [];
  if (moduleKey === "petit_dej") items = BREAKFAST_ITEMS;
  else if (moduleKey === "bain_fleurs") items = BAINFLEURS_ITEMS;
  else items = DECOR_ITEMS;
  const visible = items.filter((it) => !it.visible || it.visible(form, decorType));
  const done = visible.filter((it) => state[it.key] !== undefined && state[it.key] !== null).length;
  return { done, total: visible.length };
}

// ============ CLEANING ITEMS ============
const BASE_CLEANING = [
  { key: "linge_pret", label: "Linge prêt", kitKey: "linge" },
  { key: "panier_pret", label: "Panier prêt", kitKey: "menage" },
  { key: "buanderie_pret", label: "Buanderie prête", kitKey: "buanderie" },
  { key: "petit_dej_pret", label: "Préparation petit déj", kitKey: "petit_dejeuner" },
];

export function getCleaningItems(reservation) {
  if (!reservation) return BASE_CLEANING;
  const extras = reservation.extras || [];
  const items = [...BASE_CLEANING];
  if (extras.includes("troisieme_lit")) {
    const kitKey = reservation.troisieme_lit_type === "appoint"
      ? "troisieme_lit_appoint"
      : "troisieme_lit_parapluie";
    items.push({ key: "troisieme_lit_pret", label: "3e lit prêt", kitKey });
  }
  return items;
}
